import { useAuth0 } from '@auth0/auth0-react';
import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { openInNewTab } from '@strata/helper-functions';
import { TermsOfUseDialog } from '@strata/react-dialog';
import { Header as StrataHeader } from '@strata/react-header';
import { BaseNotification } from '@strata/react-notifications';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ComponentProps, useEffect, useMemo, useState } from 'react';

import { SwitchPayerDialog } from './dialogs/SwitchPayerDialog';

import { getHasAcceptedTermsOfUse, postAcceptTermsOfUse } from 'api/authAPI';
import { UserNotificationSettingsDialog } from 'components/header/dialogs/notificationSettings/UserNotificationSettingsDialog';
import { UserProfileDialog } from 'components/header/dialogs/UserProfileDialog';
import { FeatureCarousel } from 'components/header/FeatureCarousel';
import { NotificationComponentMap } from 'components/notifications/NotificationComponentMap';
import { clearDataForLogout } from 'services/authHelpers';
import { PayerRequestReadPermissions, Permissions } from 'services/constants';
import { handleError } from 'services/Error';
import { PayerFeatures, usePayerFeatures } from 'services/FeatureFlags';
import { AppRoutes } from 'services/routes';
import { useAppDispatch, useAppSelector } from 'store';
import { useGetHomeScreenMessagesQuery } from 'store/api/announcementApi';
import { useGetUserProfileQuery } from 'store/api/authApi';
import { useGetOpenRequestCountsQuery } from 'store/api/payerRequestApi';
import {
    clearIncomingNotification,
    updateNotificationStatus,
    updateNotificationStatuses,
} from 'store/notification/notificationThunks';

export const Header = () => {
    const { showAppVersion } = useFlags<{
        showAppVersion: boolean;
    }>();
    const { isAuthenticated, logout } = useAuth0();
    const dispatch = useAppDispatch();

    const payerFeatures = usePayerFeatures();
    const { payerId, permissions, analyticsAccess } = useAppSelector((state) => state.auth);

    const canSeePayerRequests =
        (payerFeatures?.[PayerFeatures.REQUESTS] ?? false) &&
        Object.values(PayerRequestReadPermissions).some((permission) =>
            permissions.includes(permission),
        );

    const canManagePayerRequests =
        (payerFeatures?.[PayerFeatures.REQUESTS] ?? false) &&
        Object.values(PayerRequestReadPermissions).every((permission) =>
            permissions.includes(permission),
        );

    const { data: requestCounts } = useGetOpenRequestCountsQuery(
        payerId && canSeePayerRequests ? { payerId } : skipToken,
    );

    const { data: userProfile } = useGetUserProfileQuery(undefined, {
        skip: !isAuthenticated,
    });

    const showClinicalPoliciesTab =
        (payerFeatures?.[PayerFeatures.POLICY_MAN] ?? false) &&
        permissions.includes(Permissions.READ_CLINICAL_POLICY);
    const showTeamNotificationsTab = canManagePayerRequests;
    const showAdminTab = showClinicalPoliciesTab || showTeamNotificationsTab;

    const navTabs = useMemo(() => {
        if (!isAuthenticated) {
            return [];
        }
        return [
            ...(payerFeatures?.[PayerFeatures.APREFERRAL] &&
            permissions.includes(Permissions.READ_SUMMARY)
                ? [
                      { label: 'Products', to: AppRoutes.products },
                      { label: 'Pharmacies', to: AppRoutes.pharmacies },
                      { label: 'Prescribers', to: AppRoutes.prescribers },
                  ]
                : []),
            ...(payerFeatures?.[PayerFeatures.APREFERRAL] &&
            permissions.includes(Permissions.READ_DETAIL)
                ? [{ label: 'Member Search', to: AppRoutes.treatments }]
                : []),
            ...(canSeePayerRequests
                ? [
                      {
                          label: 'Requests',
                          to: AppRoutes.requests,
                          badgeCount: Object.values(requestCounts ?? {}).reduce(
                              (sum, num) => sum + num,
                              0,
                          ),
                      },
                  ]
                : []),
            ...(showAdminTab
                ? [
                      {
                          label: 'Admin',
                          to: AppRoutes.admin,
                          children: [
                              ...(showClinicalPoliciesTab
                                  ? [{ label: 'Clinical Policies', to: AppRoutes.clinicalPolicies }]
                                  : []),
                              ...(showTeamNotificationsTab
                                  ? [
                                        {
                                            label: 'Team Notifications',
                                            to: AppRoutes.teamNotifications,
                                        },
                                    ]
                                  : []),
                          ],
                      },
                  ]
                : []),
            ...(analyticsAccess
                ? [
                      {
                          label: 'Analytics',
                          to: process.env.REACT_APP_TABLEAU_LINK,
                          openInNewTab: true,
                          icon: IconNames.SHARE,
                      },
                  ]
                : []),
        ];
    }, [
        isAuthenticated,
        payerFeatures,
        permissions,
        canSeePayerRequests,
        requestCounts,
        showAdminTab,
        showClinicalPoliciesTab,
        showTeamNotificationsTab,
        analyticsAccess,
    ]);

    const showSwitchPayerOption = Object.keys(userProfile?.payers ?? {}).length > 1;

    const [showUserNotificationSettingsDialog, setShowUserNotificationSettingsDialog] =
        useState(false);
    const [showUserProfileDialog, setShowUserProfileDialog] = useState(false);
    const [showSwitchPayerDialog, setShowSwitchPayerDialog] = useState(false);

    // Load home screen messages & carousels on login
    useGetHomeScreenMessagesQuery(undefined, { skip: !isAuthenticated });

    // Check if user has accepted terms of use on login
    const [showTermsDialog, setShowTermsDialog] = useState(false);
    useEffect(() => {
        void (async () => {
            try {
                if (isAuthenticated) {
                    const result = await getHasAcceptedTermsOfUse();
                    setShowTermsDialog(!result?.accepted);
                }
            } catch (err) {
                handleError('Error retrieving terms acceptance status', err as Error);
            }
        })();
    }, [isAuthenticated, dispatch]);

    const onAcceptTermsClick = () => {
        void (async () => {
            try {
                setShowTermsDialog(false);
                await postAcceptTermsOfUse();
            } catch (err) {
                handleError('Error accepting terms.', err as Error);
            }
        })();
    };

    const { notifications, incomingNotification } = useAppSelector((state) => state.notification);

    // Prevent incoming notifications from re-appearing
    useEffect(() => {
        dispatch(clearIncomingNotification());
    }, [incomingNotification, dispatch]);

    const onNotificationsDropdownClose = () => {
        const newlySeenIds = notifications
            .filter((notification) => notification.status === BaseNotification.Statuses.NEW)
            .map((notification) => notification.id);
        if (newlySeenIds.length > 0) {
            dispatch(updateNotificationStatuses(BaseNotification.Statuses.SEEN, newlySeenIds));
        }
    };
    const onNotificationClick: ComponentProps<typeof StrataHeader>['onNotificationClick'] = (
        id,
    ) => {
        dispatch(updateNotificationStatus(BaseNotification.Statuses.INTERACTED, id));
    };
    const onRemoveNotificationClick: ComponentProps<
        typeof StrataHeader
    >['onRemoveNotificationClick'] = (id) => {
        dispatch(updateNotificationStatus(BaseNotification.Statuses.DELETED, id));
    };
    const onClearAllNotificationsClick = () => {
        const idList = notifications.map((notification) => notification.id);
        dispatch(updateNotificationStatuses(BaseNotification.Statuses.DELETED, idList));
    };

    const onLogOutClick = () => {
        void (async () => {
            clearDataForLogout();
            await logout({
                logoutParams: { returnTo: `${window.location.origin}${AppRoutes.loggedOut}` },
            });
        })();
    };

    return (
        <>
            <StrataHeader
                navBar={navTabs}
                onProfileClick={() => setShowUserProfileDialog(true)}
                onNotificationSettingsClick={
                    canSeePayerRequests
                        ? () => setShowUserNotificationSettingsDialog(true)
                        : undefined
                }
                onLogOutClick={onLogOutClick}
                showNotifications={isAuthenticated}
                notifications={notifications}
                incomingNotification={incomingNotification}
                NotificationComponentMap={NotificationComponentMap}
                onNotificationClick={onNotificationClick}
                onRemoveNotificationClick={onRemoveNotificationClick}
                onClearAllNotificationsClick={onClearAllNotificationsClick}
                onNotificationsDropdownClose={onNotificationsDropdownClose}
                showProfileDropdown={isAuthenticated}
                onTermsOfUseClick={() => openInNewTab(AppRoutes.termsOfUse)}
                onPrivacyPolicyClick={() => openInNewTab(AppRoutes.privacyPolicy)}
                appVersion={showAppVersion ? process.env.REACT_APP_VERSION : undefined}
                fmhEnvironmentForTag={process.env.REACT_APP_ENV}
                afterProfileMenuItems={
                    showSwitchPayerOption
                        ? [
                              {
                                  text: 'Switch Payer',
                                  onItemClick: () => setShowSwitchPayerDialog(true),
                              },
                          ]
                        : []
                }
            />
            {isAuthenticated && (
                <>
                    <TermsOfUseDialog
                        isOpen={showTermsDialog}
                        onAcceptClick={onAcceptTermsClick}
                        onSignOutClick={onLogOutClick}
                    />
                    <UserNotificationSettingsDialog
                        onSecondaryButtonClick={() => setShowUserNotificationSettingsDialog(false)}
                        isOpen={showUserNotificationSettingsDialog}
                    />
                    <UserProfileDialog
                        onSecondaryButtonClick={() => setShowUserProfileDialog(false)}
                        isOpen={showUserProfileDialog}
                    />
                    <SwitchPayerDialog
                        isOpen={showSwitchPayerDialog}
                        onSecondaryButtonClick={() => setShowSwitchPayerDialog(false)}
                    />
                    {!showTermsDialog && <FeatureCarousel />}
                </>
            )}
        </>
    );
};
